import React, { useState, useEffect, useRef } from "react";
import { CancelIconModal } from "../svgcomponents/lp-common-icons";
import { useAttempts } from "@/utils/hooks/checkAttempts";
import { getData } from "@/utils/academy-stack";
import styles from "./custom-popover.module.scss";
import sanitize from "sanitize-html";

const CustomPopover = ({
  isModalOpen = false,
  uid,
  onSubmit = () => {},
  customCancel = false,
  isNotSkippable = false,
  customCancelHandler = () => {},
  title,
  href,
  message = "Sign in to access all features",
  dynamicMessage = false,
  submitTitle = "Login",
  cancelTitle = "Maybe later",
  trigger = false,
  delay = 1000,
  closeOnOutsideClick = false,
}) => {
  const [isVisible, setIsVisible] = useState(trigger);
  const [data, setData] = useState(null);
  const modalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const { resetAttempts } = useAttempts();

  const getNudgeData = async () => {
    try {
      const nudgeData = await getData("nudge_data", "", [
        { key: "uid", value: uid },
      ]);
      return nudgeData[0][0];
    } catch (error) {
      console.error("Error fetching nudge data:", error);
      return null;
    }
  };

  useEffect(() => {
    getNudgeData().then((data) => {
      if (data) {
        setData(data);
      }
    });
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      const showTimer = setTimeout(() => {
        setIsVisible(true);
        previousFocusRef.current = document.activeElement;
        modalRef.current?.focus();
      }, delay);

      return () => {
        clearTimeout(showTimer);
      };
    }
  }, [isModalOpen, delay]);

  useEffect(() => {
    if (isModalOpen && isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
      previousFocusRef.current?.focus();
    };
  }, [isModalOpen, isVisible]);

  const handleTabKey = (e) => {
    if (!modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.key === "Tab") {
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    }
  };

  const handleCancel = () => {
    if (isNotSkippable) {
      return;
    }
    if (customCancel) {
      resetAttempts();
    }
    if (customCancelHandler) {
      customCancelHandler();
    }
    setIsVisible(false);
  };

  const handleSubmit = () => {
    if (href) {
      window.location.href = href;
    } else {
      onSubmit();
    }
    setIsVisible(false);
  };

  if (!isModalOpen || !isVisible) return null;

  return (
    <div
      className="nudge-container position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ zIndex: 10000 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      ref={modalRef}
      tabIndex="-1"
      onKeyDown={handleTabKey}
    >
      {/* Backdrop */}
      <div
        className="position-absolute top-0 start-0 w-100 h-100 bg-dark bg-opacity-50"
        style={{ backdropFilter: "blur(0px)" }}
        onClick={() => {
          if (closeOnOutsideClick) setIsVisible(false);
        }}
      />

      {/* Modal Content */}
      <div className={styles.container}>
        {/* Title & Message */}
        <div className={styles.title_container}>
          <span
            id="modal-title"
            className={styles.title}
          >
            {data?.heading || title}
          </span>
          {!isNotSkippable && (
            <button
              type="button"
              onClick={() => {
                handleCancel();
              }}
              className="p-0 m-0 size-10 cursor-pointer border-0 bg-transparent outline-focussed"
              aria-label="Close modal"
              tabIndex="0"
              autoFocus
            >
              <CancelIconModal />
            </button>
          )}
        </div>
        <div className={styles.description_container}>
          <p
            id="modal-description"
            className={styles.description}
          >
            {dynamicMessage ? message : data?.description || message}
          </p>
        </div>
        <hr className="w-100 h-1 p-0 m-0" />
        {/* Buttons - Using the new styles */}
        <div className={styles.cta_container}>
          {!isNotSkippable && (
            <button
              type="button"
              className="nudge-button m-0 text-primary fw-semibold cursor-pointer border-0 bg-transparent outline-focussed text-capitalize"
              onClick={handleCancel}
            >
              {data?.ctas?.back_cta || cancelTitle}
            </button>
          )}
          <button
            type="button"
            className="nudge-button btn btn-primary btn-sm m-0 rounded-1 outline-focussed text-capitalize"
            onClick={handleSubmit}
          >
            {data?.ctas?.continue_cta || submitTitle || "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
};

export function BSToggle(id, options) {
  const element = document.getElementById(id);
  if (!element) return;
  const modal =
    bootstrap.Modal.getInstance(element, options) ||
    new bootstrap.Modal(element, options);
  return modal.toggle();
}

export const BSCustomPopover = ({
  id,
  header,
  body,
  footerBtns,
  headerClassName,
  footerClassName,
  hideCloseBtn = false,
  modalClassName,
  maxWidth = "720px",
}) => {
  return (
    <div
      className="modal fade"
      id={id}
      aria-hidden="true"
      aria-labelledby={id}
      tabIndex="-1"
    >
      <div
        className={`modal-dialog modal-dialog-centered `}
        style={{ maxWidth: maxWidth }}
      >
        <div className={`modal-content ${modalClassName ?? ""}`}>
          <div
            className={`modal-header pt-0 border-bottom d-flex align-items-center ${headerClassName ?? ""}`}
          >
            <h5
              className="modal-title fs-4 my-3"
              id={id}
              style={{
                fontWeight: "600",
                fontSize: "clamp(1rem, 4vw, 1.5rem)",
                wordBreak: "break-word",
              }}
            >
              {header}
            </h5>
            {!hideCloseBtn && (
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            )}
          </div>
          <div
            dangerouslySetInnerHTML={{ __html: sanitize(body) }}
            className="modal-body"
          />
          <div
            className={`modal-footer py-2 border-top" ${footerClassName ?? ""}`}
            style={{ backgroundColor: "#F7F9FC" }}
          >
            <div className="d-flex flex-column-reverse flex-md-row justify-content-end gap-2 w-100">
              {footerBtns?.map((item, i) => {
                if (item?.label)
                  return (
                    <button
                      aria-label={item?.ariaLabel}
                      role="button"
                      disabled={item?.disabled}
                      key={i}
                      className={`admy-primary-btn radius-4 outline-focussed ${item?.className}`}
                      data-bs-target={id}
                      data-bs-toggle={item.isToggle ? "modal" : ""}
                      onClick={item?.onClick}
                    >
                      {item.label}
                    </button>
                  );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomPopover;
